import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Clock } from 'lucide-react';
import { gameNotice } from './oilNotice';
import { oilApi, genCmd, fmt, reasonMsg } from './oilApi';
import OilRixas from './OilRixas';
import OilMafiaRules from './OilMafiaRules';
import OilServiceBriefing from './OilServiceBriefing';
import OilOperationResult from './OilOperationResult';
import OilLimitsCarousel from './OilLimitsCarousel';
import { SVC_VIDEO } from './oilMafiaText';
import { useOilLocale } from '../../i18n/OilLocale';
import { oilArt } from '../../i18n/oilArt';
import { sfx, playClip, sfxOpen, sfxClose } from './oilSounds';

const SFX_SPEND = '/oil/sfx_gasto.mp3';
import { prioritize } from './oilPreload';
import { OilIcon, KakoCoin } from './OilIcons';

const SVC_IMG = {
  industrial_espionage: '/oil/svc_industrial_espionage.webp',
  cargo_interception: '/oil/svc_cargo_interception.webp',
  pipeline_leak: '/oil/svc_pipeline_leak.webp',
  refinery_sabotage: '/oil/svc_refinery_sabotage.webp',
  platform_incident: '/oil/svc_platform_incident.webp',
  clandestine_market_move: '/oil/svc_clandestine_market_move.webp',
  fox_exchange_vault_heist: '/oil/svc_fox_exchange_vault_heist.webp',
};

// contador de imunidade do alvo (o ataque só volta a valer quando zerar)
const Immunity = ({ until, onExpire }) => {
  const [left, setLeft] = useState(() => Math.max(0, new Date(until) - Date.now()));
  useEffect(() => {
    const id = setInterval(() => {
      const ms = Math.max(0, new Date(until) - Date.now());
      setLeft(ms);
      if (ms === 0) { clearInterval(id); onExpire?.(); }
    }, 1000);
    return () => clearInterval(id);
  }, [until, onExpire]);
  const s = Math.ceil(left / 1000);
  const txt = s >= 3600
    ? `${Math.floor(s / 3600)}h ${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}m`
    : `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
  return (
    <span data-testid="oil-target-immunity"
      className="flex shrink-0 items-center gap-1 rounded-full border border-sky-600/70 bg-sky-950/60 px-2 py-0.5 text-[9px] font-black tabular-nums text-sky-300">
      <Clock className="h-2.5 w-2.5" /> {txt}
    </span>
  );
};

// luneta de sniper sobre o avatar do alvo escolhido
const Scope = () => (
  <span className="pointer-events-none absolute inset-0" data-testid="oil-target-scope">
    <span className="absolute inset-0 rounded-full bg-black/40" />
    <svg viewBox="0 0 40 40" className="absolute inset-0 h-full w-full"
      style={{ animation: 'oil-scope 1.3s ease-in-out infinite' }}>
      <circle cx="20" cy="20" r="17.5" fill="none" stroke="#f43f5e" strokeWidth="2.2" />
      <circle cx="20" cy="20" r="7.5" fill="none" stroke="#fb7185" strokeWidth="1.1" opacity="0.85" />
      <path d="M20 1v9M20 30v9M1 20h9M30 20h9" stroke="#f43f5e" strokeWidth="2.2" />
      <circle cx="20" cy="20" r="1.7" fill="#f43f5e" />
    </svg>
    <style>{`@keyframes oil-scope {
      0%, 100% { opacity: .75; filter: drop-shadow(0 0 2px rgba(244,63,94,0.7)); }
      50% { opacity: 1; filter: drop-shadow(0 0 7px rgba(244,63,94,1)); }
    }`}</style>
  </span>
);


function WantedPoster({ row }) {
  const { locale } = useOilLocale();
  return (
    <div data-testid={`wanted-poster-${row.rank}`} className="relative mx-auto w-full max-w-[185px]" style={{ aspectRatio: '2/3' }}>
      {/* foto do procurado — ATRÁS da moldura (o card é a moldura) */}
      <div className="absolute overflow-hidden" style={{ left: '18%', top: '24%', width: '64.5%', height: '41.5%' }}>
        {row.avatar
          ? <img src={row.avatar} alt={row.nickname} className="h-full w-full object-cover" />
          : <div className="flex h-full w-full items-center justify-center bg-[#c9b787] text-6xl">🦊</div>}
      </div>
      {/* moldura POR CIMA da foto */}
      <img src={oilArt('card_procurado', locale)} alt="" className="absolute inset-0 z-[2] h-full w-full object-contain drop-shadow-2xl" />
      {/* numeral de posição */}
      <div className="absolute -left-1 -top-1 z-10 flex h-9 w-9 items-center justify-center rounded-full border-2 border-amber-300 bg-gradient-to-b from-amber-400 to-amber-700 shadow-lg" data-testid={`wanted-rank-badge-${row.rank}`}>
        <span className="text-base font-black text-black drop-shadow">{row.rank}</span>
      </div>
      {/* nome no banner de estrelas */}
      <div className="absolute z-[3] flex items-center justify-center" style={{ left: '16%', top: '66.5%', width: '68%', height: '8.5%' }}>
        <p className="truncate px-1 text-center font-black text-[#1c1206]" style={{ fontFamily: 'serif', fontWeight: 900, fontSize: 'clamp(11px, 3.6vw, 15px)' }}>
          {row.nickname}
        </p>
      </div>
      {/* respeito na área nível de perigo */}
      <div className="absolute z-[3] flex items-center justify-center gap-1 overflow-hidden" style={{ left: '28%', top: '82.5%', width: '44%', height: '6%' }}>
        <OilIcon k="respect" className="!h-[12px] shrink-0 !align-middle" />
        <p className="truncate font-black leading-none text-[#4a0d0d]" style={{ fontFamily: 'serif', fontWeight: 900, fontSize: 'clamp(10px, 3.2vw, 13px)' }}>
          {fmt(row.respect)}
        </p>
      </div>
    </div>
  );
}

function OilMafia({ state, refresh, wantedTick }) {
  const { player, config, season } = state;
  const { locale } = useOilLocale();
  const [busy, setBusy] = useState(false);
  const [wanted, setWanted] = useState([]);
  const [wantedMe, setWantedMe] = useState(null);
  const [wantedOpen, setWantedOpen] = useState(false);
  const [targets, setTargets] = useState([]);
  const [intel, setIntel] = useState([]);
  const [svcId, setSvcId] = useState(null);
  const [targetId, setTargetId] = useState('');
  const [step, setStep] = useState(1);
  const [opResult, setOpResult] = useState(null);
  const [svcOpen, setSvcOpen] = useState(false);
  const [intelOpen, setIntelOpen] = useState(false);
  const [rixas, setRixas] = useState(null);
  const [rixasOpen, setRixasOpen] = useState(false);
  const [rixasTab, setRixasTab] = useState('made');
  const [revengeId, setRevengeId] = useState(null);
  const [shieldOpen, setShieldOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [infoSvc, setInfoSvc] = useState(null);
  const [posterIdx, setPosterIdx] = useState(0);
  const [prizesOpen, setPrizesOpen] = useState(false);

  const [tq, setTq] = useState('');
  const targetsRef = useRef([]);
  const qRef = useRef('');
  const [tHasMore, setTHasMore] = useState(false);
  const [tLoading, setTLoading] = useState(false);

  const loadTargets = useCallback(async (q, append = false) => {
    qRef.current = q || '';
    setTLoading(true);
    try {
      const skip = append ? targetsRef.current.length : 0;
      const params = { skip, limit: 20 };
      if (q) params.q = q;
      const r = await oilApi.get('/targets', { params });
      const rows = r.data.targets || [];
      setTargets(append ? [...targetsRef.current, ...rows] : rows);
      setTHasMore(!!r.data.has_more);
    } catch {}
    setTLoading(false);
  }, []);
  useEffect(() => { targetsRef.current = targets; }, [targets]);

  const loadAll = useCallback(async () => {
    try {
      const [r, i, a] = await Promise.all([
        oilApi.get('/rankings'), oilApi.get('/intel'), oilApi.get('/attacks')]);
      setWanted(r.data.wanted || []);
      setWantedMe(r.data.wanted_me || null);
      setIntel(i.data.intel || []);
      setRixas(a.data);
    } catch {}
    loadTargets(qRef.current);
  }, [loadTargets]);
  useEffect(() => { loadAll(); }, [loadAll, wantedTick]);

  // busca por apelido ou KAKO ID (debounce)
  const firstSearch = useRef(true);
  useEffect(() => {
    if (firstSearch.current) { firstSearch.current = false; return; }
    const id = setTimeout(() => loadTargets(tq), 400);
    return () => clearTimeout(id);
  }, [tq, loadTargets]);

  const opsUsed = Math.max(0, Number(rixas?.attacks_today) || 0);
  const opsMax = Math.max(1, Number(rixas?.max_attacks_per_day) || 10);
  const opsLocked = !!rixas && opsUsed >= opsMax;
  const respectUsed = Math.max(0, Number(rixas?.respect_today) || 0);
  const respectMax = Math.max(1, Number(rixas?.max_respect_per_day) || 600);
  const respectLocked = !!rixas && respectUsed >= respectMax;
  const [resetIn, setResetIn] = useState('--:--:--');
  useEffect(() => {
    if (!(opsLocked || respectLocked) || !rixas?.day_reset_at) return;
    const target = new Date(rixas.day_reset_at).getTime();
    const tick = () => {
      const left = target - Date.now();
      if (left <= 0) { setResetIn('00:00:00'); loadAll(); return; }
      const s = Math.floor(left / 1000);
      setResetIn([Math.floor(s / 3600), Math.floor((s % 3600) / 60), s % 60]
        .map((n) => String(n).padStart(2, '0')).join(':'));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [opsLocked, respectLocked, rixas?.day_reset_at, loadAll]);

  const openRixas = async () => {
    setRixasOpen(true);
    setRixas((r) => (r ? { ...r, unseen: 0 } : r));
    try { await oilApi.post('/attacks/seen'); } catch {}
  };

  const posters = wanted.filter((w) => w.poster);
  useEffect(() => {
    if (posters.length < 2) return;
    const t = setInterval(() => setPosterIdx((i) => (i + 1) % posters.length), 6000);
    return () => clearInterval(t);
  }, [posters.length]);

  const services = config.services || [];
  const wantedPrizes = (config.wanted?.prizes || []).filter((p) => Number(p.coins) > 0);
  const svc = services.find((s) => s.id === svcId);
  const shield = config.shield || {};
  const truce = season?.truce;

  const buyShield = async (tier) => {
    setBusy(true);
    try {
      const r = await oilApi.post('/shield', { command_id: genCmd(), tier });
      if (r.data.ok) { playClip(SFX_SPEND); sfx.shield(); gameNotice.ok('Escudo contratado', r.data.message); refresh(); }
      else gameNotice.fail('Escudo não contratado', r.data.message || reasonMsg(r.data));
    } catch { gameNotice.fail('Erro de conexão', 'Tenta de novo em instantes.'); }
    setBusy(false);
  };

  const runService = async (serviceId, target, extra) => {
    const r = await oilApi.post('/service',
      { command_id: genCmd(), service_id: serviceId, target_kako_id: target, ...extra });
    const d = r.data;
    if (!d.ok) { gameNotice.fail('Operação recusada', reasonMsg(d)); return d; }
    playClip(SFX_SPEND); // moedas pretas pagas na contratação
    const clips = SVC_VIDEO[serviceId] || null;
    const hit = d.status === 'SUCCESS_HIT' || d.status === 'SUCCESS_BLOCKED'
      || (d.status === 'SUCCESS' && d.report);
    if (!(hit ? clips?.success : clips?.fail)) {
      if (d.status === 'SUCCESS_HIT') sfx.siren();
      else if (d.status === 'SUCCESS_BLOCKED') sfx.blocked();
      else if (d.status === 'SUCCESS' && d.report) sfx.cash();
      else sfx.fail();
    }
    setOpResult({
      data: d,
      svc: services.find((s) => s.id === serviceId) || null,
      target: targets.find((t) => t.kako_id === target) || { kako_id: target },
      video: clips,
    });
    return d;
  };

  const execService = async () => {
    if (!svcId || !targetId) { gameNotice.fail('Escolha o alvo', 'Selecione quem vai receber o serviço antes de executar.'); return; }
    setBusy(true);
    try {
      const d = await runService(svcId, targetId);
      if (d.ok) setSvcOpen(false);
      refresh(); loadAll();
    } catch (e) { gameNotice.fail('Erro de conexão', e.response?.data?.detail || 'Tenta de novo em instantes.'); }
    setBusy(false);
  };

  // REVANCHE RÁPIDA: um toque na lista de rixas devolve o golpe com o mesmo serviço
  const revenge = async (row) => {
    setRevengeId(row.attack_id);
    try {
      const d = await runService(row.service_id, row.kako_id, { revenge_of: row.attack_id });
      if (d.ok) setRixasOpen(false);
      refresh(); await loadAll();
    } catch (e) { gameNotice.fail('Erro de conexão', e.response?.data?.detail || 'Tenta de novo em instantes.'); }
    setRevengeId(null);
  };

  return (
    <div className="space-y-4 pb-4" data-testid="oil-mafia">
      {/* ===== ÍCONES (cantos superiores) ===== */}
      <div className="sticky z-30 -mb-24 flex items-start justify-between px-1"
        style={{ top: 'calc(min(100vw, 448px) * 0.165 + 6px)' }}>
        <div className="flex flex-col items-start gap-1.5">
        <button onClick={() => { playClip('/oil/sfx_escudo.mp3'); setShieldOpen(true); }} data-testid="oil-shield-btn"
          className="relative h-12 w-12 transition-transform active:scale-90">
          <img src="/oil/ic_escudos_trio.webp" alt="Escudo" className="h-full w-full object-contain drop-shadow-2xl" />
          <span className="absolute -bottom-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-sky-500 px-1 text-[10px] font-black text-black shadow" data-testid="oil-shield-count">
            {player.shields || 0}
          </span>
        </button>
        <button onClick={() => { sfxOpen(); setRulesOpen(true); }} data-testid="oil-rules-btn"
          className="relative h-14 w-14 transition-transform active:scale-90">
          <img src="/oil/ic_regras.webp" alt="Regras da Máfia" className="h-full w-full object-contain drop-shadow-2xl" />
        </button>
        </div>
        <div className="flex flex-col items-end gap-1.5">
        <button onClick={() => { playClip('/oil/sfx_sirene.mp3'); setWantedOpen(true); }} data-testid="oil-wanted-btn"
          className="relative h-12 w-12 transition-transform active:scale-90"
          style={{ animation: 'warnpulse 2.4s ease-in-out infinite' }}>
          <img src={oilArt('ic_procurados', locale)} alt="Procurados" className="h-full w-full object-contain drop-shadow-2xl" />
          {wanted.length > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-black text-white shadow">
              {wanted.length}
            </span>
          )}
        </button>
        <button onClick={() => { sfxOpen(); setIntelOpen(true); }} data-testid="oil-intel-btn"
          className="relative h-11 w-11 transition-transform active:scale-90">
          <img src="/oil/ic_informantes.webp" alt="Informantes" className="h-full w-full object-contain drop-shadow-2xl" />
          {intel.length > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-black text-black shadow">
              {intel.length}
            </span>
          )}
        </button>
        <button onClick={() => { sfxOpen(); openRixas(); }} data-testid="oil-rixas-btn"
          className="relative h-11 w-11 transition-transform active:scale-90">
          <img src="/oil/ic_rixas.webp" alt="Rixas" className="h-full w-full object-contain drop-shadow-2xl" />
          {(rixas?.unseen || 0) > 0 && (
            <span data-testid="oil-rixas-badge"
              className="absolute -right-1 -top-1 flex h-5 min-w-5 animate-pulse items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-black text-white shadow">
              {rixas.unseen}
            </span>
          )}
        </button>
        </div>
      </div>

      {rulesOpen && <OilMafiaRules config={config} onClose={() => setRulesOpen(false)} />}

      {rixasOpen && (
        <OilRixas data={rixas} tab={rixasTab} onTab={setRixasTab}
          services={services} truce={truce} onRevenge={revenge} revengeId={revengeId}
          onClose={() => setRixasOpen(false)} />
      )}

      {/* ===== OVERLAY INFORMANTES ===== */}
      {intelOpen && createPortal(
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/85 backdrop-blur-sm" data-testid="oil-intel-overlay"
          onClick={() => setIntelOpen(false)}>
          <div className="w-full max-w-md p-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-[2px]" style={{ background: 'linear-gradient(160deg, #f2d387, #6e5118 40%, #c99b3f 70%, #f2d387)', borderRadius: '18px' }}>
              <div className="overflow-hidden rounded-[16px] bg-[#0b0906] p-4">
                <div className="mb-2 flex items-center justify-between">
                  <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-amber-300">
                    <img src="/oil/ic_informantes.webp" alt="" className="h-7 w-7 object-contain" /> Informantes
                  </p>
                  <button onClick={() => { sfxClose(); setIntelOpen(false); }} data-testid="oil-intel-close"
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-sm font-black text-amber-300">✕</button>
                </div>
                {intel.length === 0 ? (
                  <p className="py-4 text-center text-xs text-zinc-500">Sem novidades das ruas.</p>
                ) : (
                  <div className="max-h-[55vh] space-y-1 overflow-y-auto" data-testid="oil-intel-list">
                    {intel.map((it, i) => (
                      <p key={i} className="rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-[11px] text-zinc-300">{it.message}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ===== OVERLAY LISTA DOS PROCURADOS ===== */}
      {wantedOpen && createPortal(
        <div className="fixed inset-0 z-[95] overflow-y-auto bg-black" data-testid="oil-wanted-overlay">
          <div className="pointer-events-none fixed inset-y-0 left-1/2 w-full max-w-md -translate-x-1/2">
            <img src="/oil/bg_procurados.webp" alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-black/55" />
          </div>
          <div className="relative mx-auto max-w-md px-3 py-5 pb-16">
            <button onClick={() => { sfxClose(); setWantedOpen(false); }} data-testid="oil-wanted-close"
              className="absolute right-2 top-2 z-30 flex h-11 w-11 items-center justify-center rounded-full border border-amber-600/70 bg-black/90 text-lg font-black text-amber-300 active:scale-90">
              ✕
            </button>
            <img src={oilArt('title_procurados', locale)} alt="LISTA DOS MAIS PROCURADOS" className="pointer-events-none relative z-10 mx-auto -mb-7 mt-1 w-full max-w-[320px] drop-shadow-2xl" />

            {wantedPrizes.length > 0 && (
              <div className="relative z-20 mb-1 mt-6 flex justify-end">
                <button onClick={() => { playClip('/oil/sfx_recompensa.mp3'); setPrizesOpen(true); }} data-testid="oil-wanted-prizes-btn"
                  className="flex flex-col items-center gap-0.5 transition-transform active:scale-90">
                  <img src="/oil/ic_recompensas.webp" alt="" className="h-12 w-12 object-contain"
                    style={{ animation: 'prize-neon 2s ease-in-out infinite' }} />
                  <style>{`@keyframes prize-neon {
                    0%, 100% { filter: drop-shadow(0 0 3px rgba(255,190,60,0.5)); }
                    50% { filter: drop-shadow(0 0 9px rgba(255,215,110,0.95)) drop-shadow(0 0 18px rgba(255,150,0,0.6)); }
                  }`}</style>
                  <span className="rounded-full border border-amber-700/70 bg-black/80 px-2 py-0.5 text-[8px] font-black uppercase tracking-[0.15em] text-amber-300">Recompensas</span>
                </button>
              </div>
            )}

            <div data-testid="wanted-frame" className="relative mx-auto w-full"
              style={{
                borderStyle: 'solid',
                borderWidth: '32px 24px 21px 23px',
                borderImage: "url('/oil/frame_procurados.webp') 22.6% 9.2% 14.6% 9% round",
              }}>
              <div className="bg-[#0b0a08] px-1 py-2">
                {posters.length === 0 ? (
                  <p className="px-4 py-6 text-center text-xs font-bold text-zinc-300">
                    Nenhum mafioso fez barulho ainda... seja o primeiro. 🔫
                  </p>
                ) : (
                  <div data-testid="wanted-top3">
                    <div key={posters[posterIdx % posters.length].kako_id} style={{ animation: 'poster-in 0.55s ease-out' }}>
                      <WantedPoster row={posters[posterIdx % posters.length]} />
                    </div>
                    {posters.length > 1 && (
                      <div className="mt-2 flex justify-center gap-1.5">
                        {posters.map((p, i) => (
                          <button key={p.kako_id} onClick={() => setPosterIdx(i)} data-testid={`wanted-dot-${i + 1}`} aria-label={`cartaz ${i + 1}`}
                            className={`h-1.5 rounded-full transition-all ${i === posterIdx % posters.length ? 'w-6 bg-amber-400' : 'w-1.5 bg-zinc-400/50'}`} />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Lista 4+ — moldura de balas com scroll */}
            {wanted.length > 3 && (
              <div className="relative mx-auto -mt-5 w-full" data-testid="wanted-list-frame"
                style={{
                  borderStyle: 'solid',
                  borderWidth: '28px 23px 23px 23px',
                  borderImage: "url('/oil/frame_bullets.webp') 16.8% 13.5% 13.4% 13.4% round",
                }}>
                <div className="max-h-[250px] overflow-y-auto bg-[#0b0a08] px-1 py-1" data-testid="wanted-scroll-list">
                  <p className="pb-1 pt-0.5 text-center text-[8px] font-black uppercase tracking-[0.25em] text-amber-600"
                    data-testid="wanted-list-count">
                    Todos os procurados · {wanted.length}
                  </p>
                  {wanted.slice(3).map((w) => (
                    <div key={w.kako_id} data-testid={`wanted-row-${w.rank}`}
                      className={`mb-1 flex items-center gap-2 rounded-lg border px-2 py-1.5 ${w.rank <= 3 ? 'border-amber-700/60 bg-gradient-to-r from-amber-950/70 to-black' : 'border-zinc-800 bg-zinc-900/50'}`}>
                      <span className={`w-7 shrink-0 text-center text-[11px] font-black ${w.rank <= 3 ? 'text-amber-300' : 'text-zinc-400'}`}>{w.rank}º</span>
                      <div className="h-7 w-7 shrink-0 overflow-hidden rounded-full border border-amber-900/60">
                        {w.avatar
                          ? <img src={w.avatar} alt="" className="h-full w-full object-cover" />
                          : <div className="flex h-full w-full items-center justify-center bg-zinc-900 text-xs">🦊</div>}
                      </div>
                      <p className="min-w-0 flex-1 truncate text-[11px] font-black text-amber-100">{w.nickname}</p>
                      {w.rank <= 3 && <span className="shrink-0 text-[10px]">🏆</span>}
                      <span className="shrink-0 text-[10px] font-black text-amber-300"><OilIcon k="respect" /> {fmt(w.respect)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* SUA POSIÇÃO — logo abaixo do ranking */}
            {wantedMe && (
              <div className="mt-2 rounded-xl border-2 border-amber-600/70 bg-gradient-to-r from-[#1a1204] to-black px-3 py-2.5" data-testid="wanted-my-position">
                <p className="text-[9px] font-black uppercase tracking-[0.3em] text-amber-500">Sua posição</p>
                <div className="mt-1 flex items-center gap-2.5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-amber-600/60 bg-amber-500/15 text-sm font-black text-amber-300" data-testid="wanted-my-rank">
                    {wantedMe.rank ? `${wantedMe.rank}º` : '—'}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-black text-amber-100"><OilIcon k="respect" /> {fmt(wantedMe.respect)} de respeito</p>
                    {wantedMe.rank === 1
                      ? <p className="text-[10px] font-bold text-amber-400">Você é o Inimigo Público Nº 1! 👑</p>
                      : <p className="text-[10px] text-zinc-400">Faltam <b className="text-amber-300">{fmt(wantedMe.gap_next)}</b> de respeito para subir de posição</p>}
                    {(!wantedMe.rank || wantedMe.rank > 3) && (
                      <p className="text-[10px] text-zinc-400">Faltam <b className="text-amber-300">{fmt(wantedMe.gap_top3)}</b> de respeito para entrar no Top 3 premiado 🏆</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* ===== MODAL RECOMPENSAS DA SEASON ===== */}
      {prizesOpen && createPortal(
        <div className="fixed inset-0 z-[97] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm"
          data-testid="oil-prizes-modal" onClick={() => setPrizesOpen(false)}>
          <div className="w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="p-[2px]" style={{ background: 'linear-gradient(160deg, #f2d387, #6e5118 40%, #c99b3f 70%, #f2d387)', borderRadius: '18px' }}>
              <div className="relative overflow-hidden rounded-[16px] bg-[#0b0906] p-4">
                <button onClick={() => { sfxClose(); setPrizesOpen(false); }} data-testid="oil-prizes-close"
                  className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full border border-amber-700/70 bg-black/80 text-sm font-black text-amber-300 active:scale-90">✕</button>
                <p className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.2em] text-amber-300">
                  <img src="/oil/ic_recompensas.webp" alt="" className="h-9 w-9 object-contain drop-shadow-lg" /> Recompensas
                </p>
                <p className="mt-0.5 text-[9px] font-black uppercase tracking-[0.25em] text-amber-700">
                  Prêmio da Season · moedas de verdade
                </p>
                <div className="mt-3 space-y-1.5" data-testid="oil-prizes-list">
                  {wantedPrizes.map((p) => (
                    <div key={p.rank} data-testid={`oil-wanted-prize-${p.rank}`}
                      className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 ${p.rank === 1 ? 'border-amber-500/80 bg-gradient-to-r from-[#1e1405] to-black' : 'border-amber-900/70 bg-black/50'}`}>
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-amber-700/70 bg-amber-500/10 text-[11px] font-black text-amber-300">
                        {p.rank}º
                      </span>
                      <p className="flex-1 text-sm font-black text-amber-200"><KakoCoin /> {fmt(p.coins)}</p>
                      <span className="text-[9px] font-bold uppercase text-zinc-500">moedas KAKO</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 rounded-xl border border-amber-950 bg-black/40 px-3 py-2.5">
                  <p className="text-[10px] text-zinc-300">
                    Os <b className="text-amber-300">3 primeiros da Lista dos Mais Procurados</b> recebem o valor
                    na conta KAKO assim que o ranking encerrar.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      <img src={oilArt('title_mafia', locale)} alt="Terra dos Mafiosos" data-testid="oil-mafia-title"
        style={{ marginTop: '-9rem' }}
        className="mx-auto mb-2 w-[56%] max-w-[230px] drop-shadow-2xl" />
      {truce && (
        <div className="rounded-xl border border-blue-800 bg-blue-950/40 p-3 text-center" data-testid="oil-truce-banner">
          <p className="text-xs font-black text-blue-300">🕊️ TRÉGUA FINAL — ataques suspensos até o fim da Season</p>
        </div>
      )}

      {/* ===== MODAL ESCUDO ===== */}
      {shieldOpen && createPortal(
        <div className="fixed inset-0 z-[96] flex items-center justify-center bg-black/85 backdrop-blur-sm" data-testid="oil-shield-modal"
          onClick={() => !busy && setShieldOpen(false)}>
          <div className="w-full max-w-sm p-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-[2px]" style={{ background: 'linear-gradient(160deg, #f2d387, #6e5118 40%, #c99b3f 70%, #f2d387)', borderRadius: '18px' }}>
              <div className="overflow-hidden rounded-[16px] bg-[#0b0906] p-4 text-center">
                <button onClick={() => { sfxClose(); setShieldOpen(false); }} data-testid="oil-shield-close"
                  className="absolute right-7 mt-1 flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-sm font-black text-amber-300">✕</button>
                <div className="mt-1 grid grid-cols-3 gap-2">
                  {(shield.tiers || []).slice().reverse().map((t) => {
                    const owned = player.shield_tiers
                      ? !!player.shield_tiers[t.id]
                      : (player.shields || 0) >= { bronze: 1, prata: 2, ouro: 3 }[t.id];
                    const neon = { ouro: '#ffd24a', prata: '#d8e6ff', bronze: '#ff9a3c' }[t.id];
                    return (
                      <div key={t.id} className="flex flex-col items-center" data-testid={`oil-shield-slot-${t.id}`}>
                        <img src={`/oil/escudo_${t.id}.webp`} alt={t.name}
                          className="h-16 object-contain transition-all duration-500"
                          style={owned
                            ? { filter: `drop-shadow(0 0 6px ${neon}) drop-shadow(0 0 14px ${neon}aa)`, animation: 'shield-neon 2.2s ease-in-out infinite' }
                            : { filter: 'grayscale(1) brightness(0.45)', opacity: 0.45 }} />
                        <p className="mt-1 text-[9px] font-black uppercase text-amber-200">{t.id}</p>
                        <p className="text-[9px] font-bold text-sky-300">{Math.round(t.block_pct * 100)}% bloqueio</p>
                        {owned ? (
                          <span className="mt-1 rounded-full bg-emerald-900/60 px-2 py-1 text-[9px] font-black text-emerald-300">ATIVO</span>
                        ) : (
                          <button disabled={busy} onClick={() => buyShield(t.id)} data-testid={`oil-buy-shield-${t.id}`}
                            className="mt-1 rounded-full bg-gradient-to-r from-sky-400 to-sky-600 px-2.5 py-1 text-[9px] font-black text-black shadow transition-transform active:scale-95">
                            <OilIcon k="black" /> {fmt(t.cost_black_coins)}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                {(() => {
                  const n = Math.min(player.shields || 0, 3);
                  const sec = [
                    { label: 'DESPROTEGIDO', cls: 'text-red-400', bar: 'linear-gradient(90deg,#7f1d1d,#ef4444)' },
                    { label: 'SEGURANÇA FRACA', cls: 'text-orange-400', bar: 'linear-gradient(90deg,#7c2d12,#fb923c)' },
                    { label: 'SEGURANÇA MÉDIA', cls: 'text-yellow-300', bar: 'linear-gradient(90deg,#854d0e,#facc15)' },
                    { label: 'SEGURANÇA MÁXIMA', cls: 'text-emerald-400', bar: 'linear-gradient(90deg,#065f46,#34d399)' },
                  ][n];
                  return (
                    <div className="mt-3" data-testid="oil-shield-security">
                      <div className="h-2.5 w-full overflow-hidden rounded-full border border-zinc-700 bg-zinc-900">
                        <div className="h-full rounded-full transition-all duration-700"
                          style={{ width: `${(n / 3) * 100}%`, background: sec.bar, boxShadow: n > 0 ? '0 0 8px rgba(255,255,255,0.25)' : 'none' }}
                          data-testid="oil-shield-security-bar" />
                      </div>
                      <p className={`mt-1.5 text-[12px] font-black uppercase tracking-[0.15em] ${sec.cls}`}>{sec.label}</p>
                    </div>
                  );
                })()}
                <div className="mt-2 space-y-1.5 rounded-xl border border-zinc-800 bg-black/50 p-3 text-left" data-testid="oil-shield-lore">
                  <p className="text-center text-sm font-black uppercase text-sky-300">🛡️ Escudos de Proteção</p>
                  <p className="text-[10px] leading-relaxed text-zinc-300">
                    Nas ruas da <b className="text-amber-300">Terra dos Mafiosos</b>, todo império é alvo. Contrate seguranças pra guardar seu petróleo:
                  </p>
                  <p className="text-[10px] leading-relaxed text-zinc-300">
                    <b className="text-orange-400">🥉 Bronze</b> — capangas de esquina. Baratos, mas seguram só <b>30%</b> dos ataques. Custo: <b className="text-amber-300"><OilIcon k="black" /> {fmt((shield.tiers || []).find((t) => t.id === 'bronze')?.cost_black_coins)}</b>
                  </p>
                  <p className="text-[10px] leading-relaxed text-zinc-300">
                    <b className="text-slate-300">🥈 Prata</b> — guarda-costas profissionais. Bloqueiam <b>60%</b> das investidas. Custo: <b className="text-amber-300"><OilIcon k="black" /> {fmt((shield.tiers || []).find((t) => t.id === 'prata')?.cost_black_coins)}</b>
                  </p>
                  <p className="text-[10px] leading-relaxed text-zinc-300">
                    <b className="text-amber-400">🥇 Ouro</b> — a elite da máfia. Proteção <b>garantida: 100%</b> de bloqueio. Custo: <b className="text-amber-300"><OilIcon k="black" /> {fmt((shield.tiers || []).find((t) => t.id === 'ouro')?.cost_black_coins)}</b>
                  </p>
                  <p className="text-[10px] leading-relaxed text-zinc-400">
                    ⚔️ Ao ser atacado, seu escudo <b className="text-zinc-200">mais forte entra na frente</b> — e é destruído no confronto, protegendo você ou não. Reponha sempre!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ===== CARROSSEL DE LIMITES DO DIA ===== */}
      <OilLimitsCarousel opsUsed={opsUsed} opsMax={opsMax} opsLocked={opsLocked}
        respectUsed={respectUsed} respectMax={respectMax} respectLocked={respectLocked}
        resetIn={resetIn} />

      {/* ===== SERVIÇOS SUJOS — cards soltos sobre o fundo ===== */}
      <div className="grid grid-cols-2 gap-2.5 pt-1">
        {services.map((s) => {
          const clip = 'polygon(9px 0, calc(100% - 9px) 0, 100% 9px, 100% calc(100% - 9px), calc(100% - 9px) 100%, 9px 100%, 0 calc(100% - 9px), 0 9px)';
          const locked = opsLocked && s.offensive;
          return (
            <button key={s.id} data-testid={`oil-svc-${s.id}`} disabled={locked}
              onClick={() => {
                if (locked) return;
                playClip('/oil/sfx_servico.mp3');
                setSvcId(s.id); setStep(1); setTargetId(''); setSvcOpen(true);
                const v = SVC_VIDEO[s.id];
                if (v?.success) prioritize(`${v.success}.webm`);
                if (v?.fail) prioritize(`${v.fail}.webm`);
              }}
              className={`relative p-[2px] text-left drop-shadow-xl transition-transform ${locked ? 'cursor-not-allowed opacity-40 grayscale' : 'active:scale-[0.97]'}`}
              style={{
                clipPath: clip,
                background: 'linear-gradient(160deg, #f2d387, #6e5118 40%, #c99b3f 70%, #f2d387)',
              }}>
              <div className="flex h-full flex-col bg-[#0b0906]" style={{ clipPath: clip }}>
                <div className="relative h-24 w-full overflow-hidden">
                  <img src={SVC_IMG[s.id]} alt={s.name} loading="lazy" className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0b0906] via-transparent to-transparent" />
                  <span role="button" tabIndex={0} data-testid={`oil-svc-info-${s.id}`}
                    aria-label={`Informações sobre ${s.name}`}
                    onClick={(e) => { e.stopPropagation(); setInfoSvc(s); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); setInfoSvc(s); } }}
                    className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full border border-amber-500/80 bg-black/80 text-[11px] font-black italic text-amber-300 shadow-lg active:scale-90">
                    i
                  </span>
                </div>
                <div className="flex flex-1 flex-col items-center px-1.5 pb-2 pt-1 text-center">
                  <p className="text-[10px] font-black uppercase leading-tight text-amber-300" style={{ textShadow: '0 1px 2px #000' }}>{s.name}</p>
                  <p className="mt-0.5 text-[8.5px] font-bold uppercase leading-tight text-zinc-100">
                    {s.offensive ? <>Dano {s.damage_pct}% do FOX Dólar do alvo{s.cargo_steal ? ' + rouba carga' : ''}{s.facility_damage ? (s.facility_damage.facility === 'refinery' ? ' + amassa a usina' : ' + amassa a envasadora') : ''}</> : <>Revela informações do alvo</>}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* ===== MODAL DO SERVIÇO ===== */}
      {svcOpen && svc && createPortal(
        <div className="fixed inset-0 z-[96] flex items-center justify-center bg-black/85 backdrop-blur-sm" data-testid="oil-svc-modal"
          onClick={() => !busy && setSvcOpen(false)}>
          <div className="w-full max-w-md p-4" onClick={(e) => e.stopPropagation()}>
            <div className="relative p-[2px]" style={{ background: 'linear-gradient(160deg, #f2d387, #6e5118 40%, #c99b3f 70%, #f2d387)', borderRadius: '18px' }}>
              <button onClick={() => { sfxClose(); setSvcOpen(false); }} data-testid="oil-svc-modal-close"
                className="absolute right-2 top-2 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-amber-700/70 bg-black/85 text-sm font-black text-amber-300 active:scale-90">✕</button>
              <div className="max-h-[86vh] overflow-y-auto rounded-[16px] bg-[#0b0906]">
                <div className="relative h-36 w-full overflow-hidden">
                  <img src={SVC_IMG[svc.id]} alt={svc.name} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0b0906] via-transparent to-transparent" />
                </div>
                <div className="px-4 pb-4 pt-2">
                  <p className="text-center text-sm font-black uppercase text-amber-300" style={{ textShadow: '0 1px 2px #000' }}>{svc.name}</p>

              {step === 1 ? (
                <>

                  <div className="mt-2 overflow-hidden rounded-xl border border-amber-900/70" data-testid="oil-svc-table">
                    <div className="flex items-center justify-between border-b border-amber-900/50 bg-black/60 px-2.5 py-1.5">
                      <span className="text-[9px] font-black uppercase tracking-wider text-zinc-500">Dano</span>
                      <span className="text-[11px] font-black text-red-400">
                        {svc.offensive ? `${svc.damage_pct}% do FOX Dólar do alvo` : 'não causa dano'}
                      </span>
                    </div>
                    {svc.facility_damage && (
                      <div className="flex items-center justify-between border-b border-amber-900/50 bg-black/50 px-2.5 py-1.5">
                        <span className="text-[9px] font-black uppercase tracking-wider text-zinc-500">
                          Dano na {svc.facility_damage.facility === 'refinery' ? 'usina' : 'envasadora'}
                        </span>
                        <span className="text-[11px] font-black text-orange-300" data-testid="oil-svc-table-facility">
                          −{svc.facility_damage.points} de integridade
                        </span>
                      </div>
                    )}
                    {svc.cargo_steal && (
                      <div className="flex items-center justify-between border-b border-amber-900/50 bg-black/50 px-2.5 py-1.5">
                        <span className="text-[9px] font-black uppercase tracking-wider text-zinc-500">Roubo de carga</span>
                        <span className="text-[11px] font-black text-amber-300" data-testid="oil-svc-table-cargo">
                          até {svc.cargo_steal.max_barrels_per_hit} barris do alvo
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between border-b border-amber-900/50 bg-black/40 px-2.5 py-1.5">
                      <span className="text-[9px] font-black uppercase tracking-wider text-zinc-500">Custo</span>                      <span className="text-[11px] font-black text-amber-300">
                        <OilIcon k="black" /> {fmt(svc.cost_black_coins)} moedas pretas
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-amber-900/50 bg-black/60 px-2.5 py-1.5">
                      <span className="text-[9px] font-black uppercase tracking-wider text-zinc-500">Taxa de sucesso</span>
                      <span className="text-[11px] font-black text-emerald-300">
                        {Math.round(Number(svc.success_probability) * 100)}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between bg-black/40 px-2.5 py-1.5">
                      <span className="text-[9px] font-black uppercase tracking-wider text-zinc-500">Respeito</span>
                      <span className="text-[11px] font-black text-rose-300">
                        {svc.offensive
                          ? `+${fmt(svc.respect_hit)} acertando · +${fmt(svc.respect_blocked)} se bloquear`
                          : `+${fmt(svc.respect_success)} no sucesso`}
                      </span>
                    </div>
                  </div>

                  <button onClick={() => setInfoSvc(svc)} data-testid="oil-svc-info-btn"
                    className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl border border-amber-800/70 bg-black/60 py-2 text-[10px] font-black uppercase tracking-wider text-amber-300 active:scale-[0.98]">
                    <span className="flex h-4 w-4 items-center justify-center rounded-full border border-amber-500/80 text-[9px] italic">i</span>
                    Ver ficha completa do serviço
                  </button>

                  <button onClick={() => setStep(2)} data-testid="oil-svc-hire"
                    className="mt-2 w-full rounded-xl bg-gradient-to-r from-amber-500 to-amber-700 py-3 text-sm font-black uppercase tracking-wider text-black shadow-lg shadow-amber-950/50 active:scale-[0.98]">
                    Contratar serviço ▸
                  </button>
                </>
              ) : (
                <>
                  <div className="mt-2 flex items-center justify-between">
                    <button onClick={() => setStep(1)} data-testid="oil-svc-back"
                      className="rounded-lg border border-zinc-700 bg-black/60 px-2.5 py-1 text-[10px] font-black uppercase tracking-wider text-zinc-300 active:scale-95">
                      ◂ Voltar
                    </button>
                    <p className="text-[9px] font-black uppercase tracking-[0.2em] text-amber-500">
                      Passo 2 · Escolha o alvo
                    </p>
                  </div>
                  <input value={tq} onChange={(e) => setTq(e.target.value)}
                    data-testid="oil-target-search" placeholder="Buscar por apelido ou KAKO ID"
                    className="mt-1.5 w-full rounded-xl border border-zinc-800 bg-black/60 px-3 py-2 text-[11px] font-bold text-zinc-100 outline-none placeholder:text-zinc-600 focus:border-amber-700" />
                  <div className="mt-1.5 max-h-64 space-y-1 overflow-y-auto rounded-xl border border-zinc-800 bg-black/50 p-1.5"
                    data-testid="oil-target-list">
                    {targets.map((t) => {
                      const on = targetId === t.kako_id;
                      const off = t.eligible === false;
                      return (
                        <button key={t.kako_id} disabled={off}
                          onClick={() => setTargetId(t.kako_id)}
                          data-testid={`oil-target-${t.kako_id}`}
                          className={`flex w-full items-center gap-2 rounded-lg border p-1.5 text-left transition-all ${
                            off ? 'border-transparent bg-zinc-900/40 opacity-45'
                              : on ? 'border-rose-500 bg-rose-950/40 shadow-[0_0_18px_rgba(244,63,94,0.35)]' : 'border-transparent bg-zinc-900/60'}`}>
                          <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[10px] font-black ${
                            on ? 'bg-rose-500 text-black' : 'bg-zinc-800 text-amber-300'}`}>
                            {t.rank}º
                          </span>
                          <span className={`relative shrink-0 overflow-hidden rounded-full border bg-black transition-all ${
                            on ? 'h-10 w-10 border-rose-500' : 'h-8 w-8 border-amber-800/60'}`}>
                            {t.avatar
                              ? <img src={t.avatar} alt="" className="h-full w-full object-cover" />
                              : <span className="flex h-full w-full items-center justify-center text-sm">🦊</span>}
                            {on && <Scope />}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className={`block truncate text-[11px] font-black ${on ? 'text-rose-100' : 'text-zinc-100'}`}>{t.nickname}</span>
                            <span className="block text-[9.5px] font-bold text-zinc-500">
                              {t.immune_until ? 'Imune — acabou de tomar um golpe'
                                : off ? t.blocked_reason : `KAKO ${t.kako_id}`}
                            </span>
                          </span>
                          {t.immune_until && <Immunity until={t.immune_until} onExpire={loadAll} />}
                          {on && (
                            <span data-testid="oil-target-locked"
                              className="shrink-0 rounded-full border border-rose-500/70 bg-rose-950/70 px-2 py-0.5 text-[8px] font-black uppercase tracking-[0.15em] text-rose-300">
                              Na mira
                            </span>
                          )}
                        </button>
                      );
                    })}
                    {!tq && tHasMore && (
                      <button onClick={() => loadTargets(qRef.current, true)} disabled={tLoading}
                        data-testid="oil-target-load-more"
                        className="mt-1 w-full rounded-lg border border-amber-900/70 bg-black/60 py-2 text-[10px] font-black uppercase tracking-wider text-amber-300 active:scale-[0.98]">
                        {tLoading ? 'Carregando...' : 'Carregar mais alvos'}
                      </button>
                    )}
                  </div>
                  <button disabled={busy || truce || !targetId} onClick={execService} data-testid="oil-exec-service"
                    className={`mt-2 w-full rounded-xl py-3 text-sm font-black transition-transform active:scale-[0.98] ${truce || !targetId ? 'bg-zinc-800 text-zinc-500' : 'bg-gradient-to-r from-rose-600 to-rose-800 text-white shadow-lg shadow-rose-950'}`}>
                    {truce ? 'TRÉGUA ATIVA' : busy ? 'EXECUTANDO...' : !targetId ? 'ESCOLHA UM ALVO' : '🎯 EXECUTAR OPERAÇÃO'}
                  </button>
                  {targets.length === 0 && <p className="mt-2 text-center text-[10px] text-zinc-500" data-testid="oil-target-empty">{tq ? 'Nenhum império encontrado com essa busca.' : 'Nenhum alvo elegível no momento.'}</p>}
                </>
              )}
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {opResult && (
        <OilOperationResult data={opResult.data} svc={opResult.svc} target={opResult.target}
          video={opResult.video} services={services} limits={config.limits}
          onClose={() => setOpResult(null)} />
      )}

      {infoSvc && (
        <OilServiceBriefing svc={infoSvc} limits={config.limits}
          onClose={() => setInfoSvc(null)} />
      )}

      <style>{`@keyframes poster-in { from { opacity: 0; transform: translateX(28px) rotate(1.5deg); } to { opacity: 1; transform: translateX(0) rotate(0); } }`}</style>
    </div>
  );
}

export default React.memo(OilMafia);
